/**
 * The design-system primitives (web-plan.md D57): the same class vocabulary
 * the desktop's `ui/src/ds/` emits, because both clients wear one
 * `clients/shared/app.css`. Only what F1–F3 screens use lives here; the JSX
 * is written from the extracted DOM contract, not re-invented.
 */
import { type JSX, Show, splitProps } from "solid-js";

export function Icon(props: {
  name: string;
  size?: number;
  weight?: number;
  filled?: boolean;
  color?: string;
  class?: string;
}): JSX.Element {
  const style = () => {
    let s = "";
    if (props.size !== undefined) s += `font-size:${props.size}px;`;
    if (props.weight !== undefined) s += `--icon-weight:${props.weight};`;
    if (props.color !== undefined) s += `color:${props.color};`;
    return s;
  };
  return (
    <span
      class={`echo-icon${props.class ? ` ${props.class}` : ""}`}
      data-filled={props.filled ? "true" : undefined}
      aria-hidden="true"
      style={style()}
    >
      {props.name}
    </span>
  );
}

type ButtonKind = "prominent" | "bordered" | "secondary" | "borderless";
type ButtonSize = "small" | "medium" | "large";

export function Button(
  props: {
    size?: ButtonSize;
    kind?: ButtonKind;
    /** Every action button carries a matching icon. */
    icon: string;
    destructive?: boolean;
    full?: boolean;
    iconOnly?: boolean;
    class?: string;
  } & JSX.ButtonHTMLAttributes<HTMLButtonElement>,
): JSX.Element {
  const [own, rest] = splitProps(props, [
    "size",
    "kind",
    "icon",
    "destructive",
    "full",
    "iconOnly",
    "class",
    "children",
  ]);
  const cls = () => {
    const parts = [
      "echo-button",
      `echo-button--${own.size ?? "medium"}`,
      `echo-button--${own.kind ?? "secondary"}`,
    ];
    if (own.iconOnly) parts.push("echo-button--icon-only");
    if (own.destructive) parts.push("echo-button--destructive");
    if (own.full) parts.push("echo-button--full-width");
    if (own.class) parts.push(own.class);
    return parts.join(" ");
  };
  const iconSize = () => ((own.size ?? "medium") === "large" ? 17 : 15);
  return (
    <button type="button" class={cls()} {...rest}>
      <Icon name={own.icon} size={iconSize()} />
      {own.children}
    </button>
  );
}

export function SegmentedControl(props: {
  items: string[];
  selected: number;
  full?: boolean;
  onSelect: (index: number) => void;
}): JSX.Element {
  return (
    <div class={`echo-segmented-control${props.full ? " echo-segmented-control--full-width" : ""}`} data-ink="true">
      <Ink class="echo-segmented-control__ink" />
      {props.items.map((label, i) => (
        <button
          type="button"
          class={`echo-segmented-control__item${props.selected === i ? " echo-segmented-control__item--selected" : ""}`}
          data-ink-here={props.selected === i ? "true" : undefined}
          onClick={() => props.onSelect(i)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function SearchField(props: {
  value: string;
  placeholder: string;
  onInput: (value: string) => void;
}): JSX.Element {
  return (
    <div class="echo-search-field">
      <Icon name="search" size={17} color="var(--labels-secondary)" />
      <input
        type="text"
        value={props.value}
        placeholder={props.placeholder}
        onInput={(e) => props.onInput(e.currentTarget.value)}
      />
      <Show when={props.value !== ""}>
        <button type="button" class="echo-search-field__clear" onClick={() => props.onInput("")}>
          <Icon name="cancel" filled />
        </button>
      </Show>
    </div>
  );
}

/**
 * The selection mark that rolls (`clients/shared/ink.js`, and «The ink» in
 * `app.css`). The same markup the desktop kit emits, for the same reason the
 * stylesheet is shared: it is a contract with one script, and a second copy of
 * it would be a second answer to "where is the chosen tab".
 *
 * The host is the caller's — whatever holds the choices — and owes the mark
 * `data-ink` on itself and `data-ink-here` on whichever child is chosen.
 * `morph` is for a host whose children are not all one width: the capsule is
 * then drawn as three pieces so that its round ends keep their radius while it
 * grows.
 */
export function Ink(props: { class?: string; morph?: boolean }): JSX.Element {
  return (
    <span
      class={`echo-ink${props.morph ? " echo-ink--morph" : ""}${
        props.class ? ` ${props.class}` : ""
      }`}
      aria-hidden="true"
    >
      <Show when={props.morph}>
        <span class="echo-ink__cap" />
        <span class="echo-ink__bar" />
        <span class="echo-ink__cap echo-ink__cap--end" />
      </Show>
    </span>
  );
}

export interface StripTab {
  key: string;
  label: string;
  badge?: number;
  dot?: boolean;
}

export function TabStrip(props: {
  tabs: StripTab[];
  selected: string;
  onSelect: (key: string) => void;
}): JSX.Element {
  return (
    <div class="echo-tab-strip" role="tablist" data-ink="true">
      <Ink class="echo-tab-strip__ink" morph />
      {props.tabs.map((tab) => (
        <button
          type="button"
          role="tab"
          class={`echo-tab-strip__tab${props.selected === tab.key ? " echo-tab-strip__tab--selected" : ""}`}
          data-ink-here={props.selected === tab.key ? "true" : undefined}
          onClick={() => props.onSelect(tab.key)}
        >
          <span class="echo-tab-strip__label">{tab.label}</span>
          <Show when={(tab.badge ?? 0) > 0}>
            <span class="echo-tab-strip__badge">{tab.badge}</span>
          </Show>
          <Show when={!((tab.badge ?? 0) > 0) && tab.dot}>
            <span class="echo-tab-strip__dot" />
          </Show>
        </button>
      ))}
    </div>
  );
}

export function Checkbox(props: {
  on: boolean;
  onToggle: () => void;
  children: JSX.Element;
}): JSX.Element {
  return (
    <button
      type="button"
      class={`echo-checkbox${props.on ? " echo-checkbox--on" : ""}`}
      onClick={() => props.onToggle()}
    >
      <span class="echo-checkbox__box">
        <Icon name="check" size={13} weight={700} />
      </span>
      <span class="echo-checkbox__label">{props.children}</span>
    </button>
  );
}

export function ActivityIndicator(props: { small?: boolean }): JSX.Element {
  return (
    <span class={`echo-activity-indicator${props.small ? " echo-activity-indicator--small" : ""}`}>
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}
